import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import countriesData from "../../data/countries.json";
import Navbar from "../../components/Navbar/Navbar";
import "./Products.css";
import Footer from "../../components/Footer/Footer";
import ChatWithUs from "../../components/ChatWithUs/ChatWithUs";
import { useInquiry } from "../../context/InquiryContext";

const ProductPage = () => {
  const location = useLocation();
  const { country, category } = location.state || {};

  const { addInquiry } = useInquiry();

  const selectedCountry = countriesData.find((c) => c.country === country);

  // All category names for this country (if any)
  const categoryNames = selectedCountry?.categories?.map((c) => c.name) || [];

  // allow viewing all categories together by using the sentinel "All Categories"
  const ALL_CATEGORIES_LABEL = "All Categories";

  // initial category selection: use router-provided category if present, otherwise All
  const [selectedCategoryName, setSelectedCategoryName] = useState(
    category ?? ALL_CATEGORIES_LABEL
  );

  useEffect(() => {
    // sync selectedCategoryName when route state changes (so opening links with state updates UI)
    setSelectedCategoryName(category ?? ALL_CATEGORIES_LABEL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, selectedCountry]);

  const selectedCategory = selectedCountry?.categories?.find(
    (cat) => cat.name === selectedCategoryName
  );

  // compute products to display: either from a specific category, or flatten all categories
  const products = useMemo(() => {
    if (!selectedCountry) return [];
    if (selectedCategoryName === ALL_CATEGORIES_LABEL) {
      // flatten all products from all categories
      return (
        selectedCountry.categories?.reduce((acc, cat) => {
          if (Array.isArray(cat.products)) acc.push(...cat.products);
          return acc;
        }, []) || []
      );
    }
    return selectedCategory?.products || [];
  }, [selectedCountry, selectedCategoryName, selectedCategory]);

  // Removed unused productCount variable to satisfy ESLint no-unused-vars

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // allow multiple selected tags
  const [selectedTags, setSelectedTags] = useState([]);
  const [requirements, setRequirements] = useState("");

  const openModal = (product) => {
    setSelectedProduct(product);
    // reset selections when opening a new product
    setSelectedTags([]);
    setRequirements("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setSelectedTags([]);
    setRequirements("");
  };

  // Close on ESC and lock body scroll while modal is open
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    if (isModalOpen) {
      document.addEventListener("keydown", onKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  // De-duplicate and normalize tags for the selected product
  const normalizedTags = useMemo(() => {
    if (!selectedProduct?.tags) return [];
    return [...new Set(selectedProduct.tags.map((t) => String(t).trim()))];
  }, [selectedProduct]);

  // toggle a tag in the selectedTags array
  const toggleTag = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) return prev.filter((t) => t !== tag);
      return [...prev, tag];
    });
  };

  // --------------------
  // Search implementation (unchanged)
  // --------------------
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceRef = useRef(null);
  const DEBOUNCE_MS = 250;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const filteredProducts = useMemo(() => {
    if (!debouncedQuery) return products;
    const q = debouncedQuery.toLowerCase();
    return products.filter((p) => {
      if (p.name && p.name.toLowerCase().includes(q)) return true;
      if (Array.isArray(p.tags)) {
        for (const t of p.tags) {
          if (String(t).toLowerCase().includes(q)) return true;
        }
      }
      if (p.Description && String(p.Description).toLowerCase().includes(q))
        return true;
      return false;
    });
  }, [products, debouncedQuery]);

  const clearSearch = () => {
    setSearchQuery("");
    setDebouncedQuery("");
  };

  // --------------------
  // Add to Inquiry handler (uses addInquiry from context)
  // --------------------
  const handleAddToInquiry = () => {
    if (!selectedProduct) return;

    // fullTags is the full tag list snapshot for this product at add-time
    const fullTags = normalizedTags;
    const payload = {
      productName: selectedProduct.name,
      product: selectedProduct.name,
      productRef: {
        id: selectedProduct?.id ?? null,
        country: country ?? null,
        category: selectedCategoryName ?? null,
      },
      // store both arrays
      fullTags: fullTags.length > 0 ? fullTags : null,
      selectedTags: selectedTags.length > 0 ? selectedTags : null,
      country: country ?? null,
      category: selectedCategoryName ?? null,
      requirements: requirements?.trim() || null,
      additionalText: requirements?.trim() || null,
      description: selectedProduct?.Description || null,
      image: selectedProduct?.Image || null,
      createdAt: new Date().toISOString(),
    };

    try {
      const created = addInquiry(payload);
      console.log("Saved inquiry item:", created);
      // close modal after adding
      closeModal();
    } catch (err) {
      console.error("Failed to save inquiry item:", err);
    }
  };

  // Add button enabled if there is any tag selected OR any text in requirements
  const canAdd = selectedTags.length > 0 || (requirements && requirements.trim().length > 0);

  return (
    <>
      <Navbar />
      <div className="product-page-container">
        {/* Top controls: category selector + search */}
        <div className="top-controls">
          {selectedCountry && categoryNames.length > 0 && (
            <div className="category-select-wrapper">
              <label htmlFor="category-select" className="category-select-label">
                Category:
              </label>
              <select
                id="category-select"
                className="category-select"
                value={selectedCategoryName}
                onChange={(e) => setSelectedCategoryName(e.target.value)}
              >
                <option value={ALL_CATEGORIES_LABEL}>{ALL_CATEGORIES_LABEL}</option>
                {categoryNames.map((cName) => (
                  <option key={cName} value={cName}>
                    {cName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="search-bar">
            <label htmlFor="product-search" className="visually-hidden">
              Search products (by name, tag or description)
            </label>
            <input
              id="product-search"
              type="search"
              placeholder="Search by name, tag, or description"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              aria-label="Search products by name or tag"
            />
            {searchQuery && (
              <button type="button" className="search-clear" aria-label="Clear search" onClick={clearSearch}>
                ×
              </button>
            )}
          </div>
        </div>

        <p className="product-count">
          Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? "s" : ""}
        </p>

        <p className="breadcrumb" aria-label="current page">
          <span className="breadcrumb-country">{country ?? "All Countries"}</span>
          <span className="breadcrumb-sep">→</span>
          <span className="breadcrumb-category">{selectedCategoryName ?? ALL_CATEGORIES_LABEL}</span>
        </p>

        <div className="product-grid">
          {filteredProducts.map((product, index) => {
            // normalize tags for preview
            const rawTags = Array.isArray(product.tags) ? product.tags : [];
            const tags = [
              ...new Set(rawTags.map((t) => String(t).trim()).filter(Boolean)),
            ];
            const previewTags = tags.slice(0, 3);
            const remainingCount = Math.max(0, tags.length - previewTags.length);

            return (
              <div
                className="product-card"
                key={product.id ?? `${product.name ?? index}-${index}`}
                onClick={() => openModal(product)}
                role="button"
                aria-label={`Open ${product.name} details`}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openModal(product);
                }}
              >
                <img src={product.Image} alt={product.name} />

                {/* small tags preview footer (below image) */}
                {tags.length > 0 && (
                  <div className="product-card-footer">
                    <p className="product-card-tags">
                      {previewTags.join(", ")}
                      {remainingCount > 0 && (
                        <span className="product-card-tags-more">, and {remainingCount} more</span>
                      )}
                    </p>
                  </div>
                )}

                <p className="product-name">{product.name}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedProduct && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="product-modal-title">
            <button className="modal-close" onClick={closeModal} aria-label="Close" title="Close">×</button>

            {/* New grid layout matching your design */}
            <div className="modal-grid">
              {/* Image (left, spans two rows) */}
              <div className="modal-image-wrapper">
                <img src={selectedProduct.Image} alt={selectedProduct.name} className="modal-image-grid" />
              </div>

              {/* Name (top-right) */}
              <div className="modal-name">
                <h2 id="product-modal-title" className="modal-title-grid">{selectedProduct.name}</h2>
              </div>

              {/* Short description (below name on right) */}
              <div className="modal-desc">
                {selectedProduct.Description ? <p className="modal-description-grid">{selectedProduct.Description}</p> : <p className="modal-description-grid muted">No description</p>}
              </div>

              {/* Info area spanning full width */}
              <div className="modal-info">
                {/* Example content: show country, category and tags summary */}
                <div className="modal-info-inner">
                  <p style={{ margin: 0, color: "#111827", fontWeight: 700 }}>Info</p>
                  <div className="modal-info-block">
                    <strong>Page:</strong> {country ?? "—"} &nbsp; · &nbsp;
                    <strong>Category:</strong> {selectedCategoryName ?? ALL_CATEGORIES_LABEL}
                  </div>

                  {/* interactive checkboxes for tags (multi-select) */}
                  {normalizedTags.length > 0 && (
                    <div className="modal-info-block tags-checkbox-group" role="group" aria-label="Product tags">
                      {normalizedTags.map((tag, i) => {
                        const id = `tag-${i}`;
                        const checked = selectedTags.includes(tag);
                        return (
                          <label key={id} className={`tag-chip ${checked ? "checked" : ""}`} htmlFor={id}>
                            <input
                              id={id}
                              type="checkbox"
                              value={tag}
                              checked={checked}
                              onChange={() => toggleTag(tag)}
                            />
                            <span className="tag-chip-label">{tag}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Requirements input (left of actions in bottom row) */}
              <div className="modal-requirements">
                <input id="requirements" className="requirements-input" placeholder="Enter your requirements" value={requirements} onChange={(e) => setRequirements(e.target.value)} />
              </div>

              {/* Actions: Add to Inquiry list (bottom-right) */}
              <div className="modal-actions-grid">
                <button type="button" className="btn-add-inquiry" onClick={handleAddToInquiry} disabled={!canAdd}>Add to Inquiry list</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <ChatWithUs />
    </>
  );
};

export default ProductPage;